import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Holding } from '../../types';
import { SavedBuyPlan, SavedSellPlan } from '../../types/savedPlans';
import { SplitBuyPlanner } from './SplitBuyPlanner';
import { SplitSellPlanner } from './SplitSellPlanner';
import { AssetComparison } from './AssetComparison';
import { SavedBuyPlansBand, SavedSellPlansBand } from './SavedPlansBand';
import { useSavedBuyPlans, useSavedSellPlans } from '../../hooks/useSavedPlans';
import { useSubscription } from '../../hooks/useSubscription';
import { StrategyPreview } from './StrategyPreview';

type StrategyTab = 'compare' | 'buy' | 'sell';

const TABS: { id: StrategyTab; label: string }[] = [
  { id: 'compare', label: 'Compare' },
  { id: 'buy',     label: 'Split Buy' },
  { id: 'sell',    label: 'Split Sell' },
];

/**
 * Strategy tab content: an underline tab row (visually identical to the Coin
 * Detail page sub-tabs) over full-width panels. All panels stay mounted and are
 * toggled with `display` so form state (Split Buy/Sell inputs, Compare pickers,
 * etc.) is preserved when switching tabs. The active sub-tab persists in the URL
 * (?sub=…) so a refresh keeps it. (Tax Plan now lives in the top-level Tax tab.)
 */
export function StrategyView({ holdings }: { holdings: Holding[] }) {
  // Sub-tab in the URL (?sub=…); changes use replace so main-tab back/forward stays clean.
  const [searchParams, setSearchParams] = useSearchParams();
  const subParam = searchParams.get('sub');
  const tab: StrategyTab = subParam === 'buy' || subParam === 'sell' ? subParam : 'compare';
  const setTab = (id: StrategyTab) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('sub', id);
        return next;
      },
      { replace: true }
    );

  // Edit requests routed from the saved-plan bands into the planners. The nonce
  // bumps on every click so re-selecting the same plan re-triggers the load effect.
  const [buyEdit,  setBuyEdit]  = useState<{ plan: SavedBuyPlan; nonce: number } | null>(null);
  const [sellEdit, setSellEdit] = useState<{ plan: SavedSellPlan; nonce: number } | null>(null);
  const editNonce = useRef(0);

  // Strategy is a Pro feature. Free users get a read-only preview instead of the
  // live planners (resolved after all hooks below, to respect the Rules of Hooks).
  const { canPlan } = useSubscription();

  // Saved plan counts — shown as muted text next to the Split Buy / Split Sell sub-tabs.
  const { plans: savedBuyPlans } = useSavedBuyPlans();
  const { plans: savedSellPlans } = useSavedSellPlans();

  // The plan id each planner currently has loaded — drives the band row highlight.
  const [activeBuyId,  setActiveBuyId]  = useState<string | null>(null);
  const [activeSellId, setActiveSellId] = useState<string | null>(null);

  // Just-saved feedback: scroll up to the saved-plans band and briefly glow the
  // new row. Cleared after the ~2s animation. Refs let us scroll to each band.
  const buyBandRef  = useRef<HTMLDivElement>(null);
  const sellBandRef = useRef<HTMLDivElement>(null);
  const [highlightBuyId,  setHighlightBuyId]  = useState<string | null>(null);
  const [highlightSellId, setHighlightSellId] = useState<string | null>(null);
  const buyTimer  = useRef<number | undefined>(undefined);
  const sellTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => {
    window.clearTimeout(buyTimer.current);
    window.clearTimeout(sellTimer.current);
  }, []);

  // Scroll + highlight must run AFTER the new plan has rendered into the band and
  // after the planner resets/shrinks (which reflows the page) — otherwise the
  // scroll targets a stale position. Two rAFs defer past React's commit + paint.
  const handleBuySaved = useCallback((planId: string) => {
    window.clearTimeout(buyTimer.current);
    setHighlightBuyId(null); // reset so re-saving the same plan restarts the flash
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        buyBandRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setHighlightBuyId(planId);
        buyTimer.current = window.setTimeout(() => setHighlightBuyId(null), 2200);
      });
    });
  }, []);

  const handleSellSaved = useCallback((planId: string) => {
    window.clearTimeout(sellTimer.current);
    setHighlightSellId(null);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        sellBandRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setHighlightSellId(planId);
        sellTimer.current = window.setTimeout(() => setHighlightSellId(null), 2200);
      });
    });
  }, []);

  // Without the planning tier, the whole Strategy tab is a read-only preview.
  // The main tab bar keeps Strategy visible/clickable (this renders as its content).
  if (!canPlan) return <StrategyPreview />;

  return (
    <div>
      {/* Underline tab row */}
      <div
        className="flex gap-1 overflow-x-auto"
        style={{ borderBottom: '0.5px solid var(--tab-row-border)' }}
      >
        {TABS.map((t) => {
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className="whitespace-nowrap transition-colors"
              style={{
                background: 'transparent',
                padding: '10px 14px',
                fontSize: '13px',
                borderBottom: isActive ? '2px solid var(--tab-accent)' : '2px solid transparent',
                marginBottom: '-0.5px',
                color: isActive ? 'var(--tab-text-active)' : 'var(--tab-text-inactive)',
                fontWeight: isActive ? 500 : 400,
              }}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = 'var(--tab-text-hover)'; }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = 'var(--tab-text-inactive)'; }}
            >
              {t.label}
              {t.id === 'buy' && savedBuyPlans.length > 0 && (
                <span className="ml-1.5 text-[11px]" style={{ color: 'var(--color-sub)' }}>{savedBuyPlans.length}</span>
              )}
              {t.id === 'sell' && savedSellPlans.length > 0 && (
                <span className="ml-1.5 text-[11px]" style={{ color: 'var(--color-sub)' }}>{savedSellPlans.length}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Panels — kept mounted, toggled via display to preserve state */}
      <div className="pt-6">
        <div style={{ display: tab === 'compare' ? 'block' : 'none' }}>
          <AssetComparison holdings={holdings} />
        </div>
        <div style={{ display: tab === 'buy' ? 'block' : 'none' }}>
          <div ref={buyBandRef} style={{ scrollMarginTop: 88 }}>
            <SavedBuyPlansBand
              activeId={activeBuyId}
              highlightId={highlightBuyId}
              onSelect={(plan) => setBuyEdit({ plan, nonce: ++editNonce.current })}
            />
          </div>
          <SplitBuyPlanner
            editRequest={buyEdit}
            onEditingChange={setActiveBuyId}
            onPlanSaved={handleBuySaved}
            active={tab === 'buy'}
          />
        </div>
        <div style={{ display: tab === 'sell' ? 'block' : 'none' }}>
          <div ref={sellBandRef} style={{ scrollMarginTop: 88 }}>
            <SavedSellPlansBand
              activeId={activeSellId}
              highlightId={highlightSellId}
              onSelect={(plan) => setSellEdit({ plan, nonce: ++editNonce.current })}
            />
          </div>
          <SplitSellPlanner
            holdings={holdings}
            editRequest={sellEdit}
            onEditingChange={setActiveSellId}
            onPlanSaved={handleSellSaved}
            active={tab === 'sell'}
          />
        </div>
      </div>
    </div>
  );
}
